import React, {useEffect, useState, useRef} from 'react';
import './index.scss';
import NotificationIcon from '../../SVG/NotificationIcon';
import { onMessage } from 'firebase/messaging';
import { firebaseMessaging } from '../../utils/firebase';
import { toast } from 'react-toastify';
import { useNotifications, useMarkAsReadNotification } from '../../hooks/useNotifications';
import { useNavigate } from 'react-router-dom';
import routes from '../../utils/routes';
import { useTranslation } from '../../hooks/useTranslation';

export default function Notification() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const { data: notifications, refetch: refetchNotifications } = useNotifications();
  const [notificationOpened, setNotificationOpened] = useState(false);
  const notificationRef = useRef(null);
  const { mutate: markAsReadNotification, isPending: isMarkAsReadPending } = useMarkAsReadNotification({
    onSuccess: () => {
      refetchNotifications();
    },
  });
  const handleIconClick = (e) => {
    setNotificationOpened(!notificationOpened);
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on the icon container
      if (event.target.closest('.notification-icon-container')) {
        return;
      }
      
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationOpened(false);
      }
    };

    if (notificationOpened) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [notificationOpened]);

  useEffect(() => {
    const unsubscribe = onMessage(firebaseMessaging, (payload) => {
      toast.info(
        `${payload?.notification?.title || t('notification.messages.noTitle')} ${payload?.notification?.body || t('notification.messages.noBody')}`
      );
      refetchNotifications();
    });
    return () => unsubscribe();
  }, []);

  const formatTimeAgo = (timeString) => {
    return timeString;
  };

  const handleMarkAsRead = (id) => {
    markAsReadNotification(id);
    navigate(`${routes.private.equipment}?active=3`);
    setNotificationOpened(false);
  };

  const handleIgnore = (id) => {
    markAsReadNotification(id);
    setNotificationOpened(false);
  };

  return (
    <div className='notification-wrapper'>
      <div className='notification-icon-container' onClick={handleIconClick}>
        <NotificationIcon />
        <div className='notification-badge'>
          <span>{notifications?.length || 0}</span>
        </div>
      </div>
      {
        notificationOpened && (
          <div className='notification' ref={notificationRef}>
            <div className='notification__header'>
              <p className='notification__title'>{t('notification.title')}</p>
            </div>
            {
              notifications?.length > 0 ? (
                <div className='notification__list'>
                  {
                    notifications?.map((notification) => (
                      <div key={notification.id} className='notification__list-elem'>
                        <div className='notification__list-content'>
                          <div className='notification__list-header'>
                            <h4 
                              className={`notification__list-title notification__list-title--${notification?.details?.action?.toLowerCase() || 'default'}`}
                            >
                              {notification?.details?.action || t('notification.defaults.dash')}
                              <span className='notification__list-equipment-type'>
                                {notification?.details?.type || t('notification.defaults.dash')}
                              </span>
                            </h4>
                            
                            <div className='notification__list-indicator'></div>
                          </div>
                          <p className='notification__list-description'>
                            {notification?.details?.message || t('notification.defaults.dash')}
                          </p>
                          <div className='notification__list-meta'>
                            <span className='notification__list-equipment'>
                              {t('notification.equipment')}: {notification?.details?.equipment || t('notification.defaults.dash')}
                            </span>
                            <span className='notification__list-time'>
                              {notification?.created_at ? formatTimeAgo(notification.created_at) : t('notification.defaults.clock')}
                            </span>
                          </div>
                          <div className='notification__list-actions'>
                            <button className='notification__btn notification__btn--primary' onClick={() => handleMarkAsRead(notification.id)} disabled={isMarkAsReadPending}>{t('notification.buttons.view')}</button>
                            <button className='notification__btn notification__btn--secondary' onClick={() => handleIgnore(notification.id)} disabled={isMarkAsReadPending}>{t('notification.buttons.ignore')}</button>
                          </div>
                        </div>
                      </div>
                    ))
                  }
                </div>
              ) : (
                <div className='notification__list-elem'>
                  <p className='notification__list-description'>{t('notification.noNotifications')}</p>
                </div>
              )
            }
          </div>
        )
      }
    </div>
  );
}
